/**
 * Spec 052: clients, their orders and an order's code — the requests of step 6's screens. The
 * routes live under /departments (the usual MCP address is full); approving a document, marking
 * a payment and downloading the source are a person's, here in the app. Spec 054: the client's
 * chat of an order — a person answers it and sends a document here; step 3: the client's passport.
 */
import { api, fetchBlob } from "@/api/client";
import type { CodeReview } from "@/api/code";
import type { DocumentKind, DocumentState, Stage } from "@/lib/orders";

export interface Ref { id: string; name: string }
/** Spec 061: a file of the order; people download it by its id. */
export interface FileRef { id: string; name: string; mime: string; size: number }

export interface ClientOut {
  id: string;
  name: string;
  kind: "company" | "person";
  inn: string | null;
  about_md: string | null;
  contact_ref: string | null;
  version: number;
  orders: number;
  created_by: string;
  via_agent: boolean;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface MilestoneOut {
  id: string;
  position: number;
  title: string;
  planned_at: string | null;
  done_at: string | null;
  status_note: string | null;
  client_visible: boolean;
  version: number;
}

export interface DocumentOut {
  id: string;
  kind: DocumentKind;
  revision: number;
  label: string;
  title: string;
  body_md: string;
  proposed_amount_rub: number | null;
  state: DocumentState;
  decided_by: Ref | null;
  /** Spec 061: «Юрист» decided it in its run. */
  decided_by_agent?: boolean;
  decided_at: string | null;
  decision_note: string | null;
  /** Spec 061: the file the client got last, and its signed copy. */
  file?: FileRef | null;
  sent_at?: string | null;
  signed_file?: FileRef | null;
  signed_at?: string | null;
  version: number;
  created_by: string;
  via_agent: boolean;
  created_at: string;
}

/** Spec 061: a document as the client's page lists them, with its order. */
export interface ClientDocumentOut extends DocumentOut {
  order: { id: string; label: string; title: string; stage: Stage };
}

export interface UnitWorkRef { id: string; number: number; title: string; state: string; unit_name: string; assignee: string }

export interface EventOut {
  id: string;
  kind: string;
  from_value: string | null;
  to_value: string | null;
  note_md: string | null;
  actor_id: string;
  actor_name: string | null;
  via_agent: boolean;
  created_at: string;
}

export interface OrderOut {
  id: string;
  number: number;
  label: string;
  title: string;
  client: Ref;
  area: Ref;
  stage: Stage;
  lost_reason: string | null;
  manager: Ref | null;
  owner: Ref;
  brief_md: string | null;
  amount_rub: number | null;
  paid_rub: number | null;
  version: number;
  milestones: MilestoneOut[];
  documents: DocumentOut[];
  unit_orders: UnitWorkRef[];
  events: EventOut[];
  created_by: string;
  via_agent: boolean;
  created_at: string;
  updated_at: string;
}

export interface OrderPublic { label: string; title: string; stage: string; milestones: { title: string; planned_at: string | null; done_at: string | null; status_note: string | null }[] }

export interface OrderCodeState {
  opened: boolean;
  order_id: string;
  opens_at?: string;
  head?: string;
  default_branch?: string;
  created?: boolean;
}

/** A review of an order's code: `task_id` is the unit order's, as `unit_order_id`. */
export interface OrderCodeReview extends CodeReview {
  unit_order_id: string;
  order_id?: string;
  order_label?: string;
  order_title?: string;
}

/** Spec 054: a file the client sent with a message — what it is; people download it by its id. */
export interface ChatFileOut { id: string; name: string; mime: string; size: number }

/** Spec 054: one message of an order's chat with its client, as people read it. */
export interface ClientMessageOut {
  id: string;
  direction: "in" | "out";
  author: string;
  from_agent_id: string | null;
  sent_by: Ref | null;
  document_id: string | null;
  body_md: string;
  attachment_ids: string[];
  files: ChatFileOut[];
  masked: boolean;
  status: "received" | "queued" | "sent" | "failed";
  error: string | null;
  created_at: string;
  sent_at: string | null;
  read_at: string | null;
}

export interface ClientChatOut {
  order_id: string;
  label: string;
  client: Ref;
  stage: Stage;
  manager: Ref | null;
  bound: boolean;
  messages: ClientMessageOut[];
  outgoing_in_a_row: number;
  monologue_limit: number;
}

export interface InviteOut { url: string; expires_at: string }

/** Spec 054, step 3: what we know of a client, to a template, in versions of its own. */
export interface ClientPassportOut {
  client_id: string;
  client: string;
  body_md: string;
  version: number;
  updated_at: string | null;
  updated_by: string | null;
  agent: string | null;
  via_agent: boolean;
  source_md: string | null;
  template_md: string | null;
}

export interface ClientPassportRevisionOut {
  id: string;
  version: number;
  body_md: string;
  source_md: string | null;
  author: string;
  agent: string | null;
  via_agent: boolean;
  created_at: string;
}

/** Spec 062: the client's dossier — what is not a fact of the passport. */
export type ClientDossierOut = ClientPassportOut;

/** Spec 062: the summary of an order's chat, how far it reaches and whether the manager owes it. */
export interface ChatSummaryOut extends ClientPassportOut {
  order_id: string;
  label: string;
  through_message_id: string | null;
  through_at: string | null;
  behind: number;
  due: boolean;
}

export interface ClientIn { name: string; inn?: string | null; about_md?: string | null; contact_ref?: string | null }
export interface OrderIn { title: string; client_id: string; area_id: string; brief_md?: string | null; manager_agent_id?: string | null; owner_user_id?: string | null }
export interface MilestoneIn { title: string; planned_at?: string | null; status_note?: string | null; client_visible?: boolean }
export interface DocumentIn { kind: DocumentKind; title: string; body_md: string; proposed_amount_rub?: number | null }

const CLIENTS = "/api/v1/departments/clients";
const ORDERS = "/api/v1/departments/client-orders";
const create = <T>(url: string, body: unknown) => api<T>(url, { method: "POST", body, idempotent: true });
const post = <T>(url: string, body: unknown) => api<T>(url, { method: "POST", body });
const patch = <T>(url: string, body: unknown) => api<T>(url, { method: "PATCH", body });

export const Clients = {
  list: (includeArchived = false) => api<ClientOut[]>(`${CLIENTS}${includeArchived ? "?include_archived=true" : ""}`),
  get: (id: string) => api<ClientOut>(`${CLIENTS}/${id}`),
  create: (body: ClientIn) => create<ClientOut>(CLIENTS, body),
  update: (id: string, body: Partial<ClientIn> & { version: number; archived?: boolean }) => patch<ClientOut>(`${CLIENTS}/${id}`, body),
  /** Spec 061: every document of the client's orders. */
  documents: (id: string) => api<ClientDocumentOut[]>(`${CLIENTS}/${id}/documents`),
};

export const Orders = {
  list: (filter: { client_id?: string; area_id?: string; manager_agent_id?: string } = {}) => {
    const query = new URLSearchParams(Object.entries(filter).filter(([, v]) => Boolean(v)) as [string, string][]).toString();
    return api<OrderOut[]>(`${ORDERS}${query ? `?${query}` : ""}`);
  },
  get: (id: string) => api<OrderOut>(`${ORDERS}/${id}`),
  create: (body: OrderIn) => create<OrderOut>(ORDERS, body),
  update: (id: string, body: { version: number; title?: string; brief_md?: string | null; owner_user_id?: string }) => patch<OrderOut>(`${ORDERS}/${id}`, body),
  move: (id: string, body: { version: number; stage: Stage; reason_md?: string | null }) => post<OrderOut>(`${ORDERS}/${id}/stage`, body),
  handOver: (id: string, body: { version: number; manager_agent_id: string | null; note_md?: string | null }) => post<OrderOut>(`${ORDERS}/${id}/hand-over`, body),
  addMilestone: (id: string, body: MilestoneIn) => create<MilestoneOut>(`${ORDERS}/${id}/milestones`, body),
  updateMilestone: (id: string, milestone: string, body: Partial<MilestoneIn> & { version: number; done_at?: string | null }) =>
    patch<MilestoneOut>(`${ORDERS}/${id}/milestones/${milestone}`, body),
  addDocument: (id: string, body: DocumentIn) => create<DocumentOut>(`${ORDERS}/${id}/documents`, body),
  decideDocument: (id: string, document: string, body: { version: number; decision: "approved" | "rejected"; note_md?: string | null }) =>
    post<DocumentOut>(`${ORDERS}/${id}/documents/${document}/decision`, body),
  payment: (id: string, body: { version: number; amount_rub: number; note_md?: string | null }) => post<OrderOut>(`${ORDERS}/${id}/payments`, body),
  /** Spec 061: the client sent the contract back signed — one of its files in the chat. */
  signed: (id: string, document: string, body: { version: number; attachment_id: string }) =>
    post<DocumentOut>(`${ORDERS}/${id}/documents/${document}/signed`, body),
  public: (id: string) => api<OrderPublic>(`${ORDERS}/${id}/public`),
};

const code = (order: string) => `${ORDERS}/${order}/code`;
const review = (order: string, item: { id: string; unit_order_id: string }) =>
  `${code(order)}/reviews/${item.id}?unit_order_id=${encodeURIComponent(item.unit_order_id)}`;

export const OrderCode = {
  state: (order: string) => api<OrderCodeState>(code(order)),
  open: (order: string) => post<OrderCodeState>(code(order), {}),
  reviews: (order: string) => api<{ reviews: OrderCodeReview[]; truncated: boolean }>(`${code(order)}/reviews`),
  review: (order: string, item: { id: string; unit_order_id: string }) => api<OrderCodeReview>(review(order, item)),
  reviewDiff: (order: string, item: { id: string; unit_order_id: string }, path: string) =>
    api<{ diff: string; truncated: boolean; files?: string[]; files_truncated?: boolean }>(`${code(order)}/reviews/${item.id}/diff?unit_order_id=${encodeURIComponent(item.unit_order_id)}&path=${encodeURIComponent(path)}`),
  decide: (order: string, item: OrderCodeReview, decision: "approve" | "reject", comment: string) =>
    api<OrderCodeReview>(`${code(order)}/reviews/${item.id}/decision?unit_order_id=${encodeURIComponent(item.unit_order_id)}`, {
      method: "POST",
      body: { expected_revision: item.revision, decision, comment },
      idempotent: true,
    }),
  bundle: (order: string) => fetchBlob(`${code(order)}/bundle`),
  forMe: () => api<{ reviews: OrderCodeReview[]; truncated: boolean }>(`${ORDERS}/code-reviews/for-me`),
};

/** Spec 054: the order's chat — the last messages, older ones page by page with `before`. */
export const CHAT_PAGE = 50;

export const ClientChat = {
  get: (order: string, before?: string) =>
    api<ClientChatOut>(`${ORDERS}/${order}/chat?limit=${CHAT_PAGE}${before ? `&before=${encodeURIComponent(before)}` : ""}`),
  answer: (order: string, body_md: string) => create<ClientMessageOut>(`${ORDERS}/${order}/chat/messages`, { body_md }),
  invite: (order: string) => post<InviteOut>(`${ORDERS}/${order}/chat/invite`, {}),
  revoke: (order: string) => post<ClientChatOut>(`${ORDERS}/${order}/chat/revoke`, {}),
  sendDocument: (order: string, document: string, body: { version: number; approve?: boolean }) =>
    post<ClientMessageOut[]>(`${ORDERS}/${order}/documents/${document}/send`, body),
  file: (id: string) => fetchBlob(`/api/v1/attachments/${id}`),
};

/** Spec 054, step 3: the client's passport and its saved texts. */
export const ClientPassports = {
  get: (client: string) => api<ClientPassportOut>(`${CLIENTS}/${client}/passport`),
  save: (client: string, body: { body_md: string; version: number }) => patch<ClientPassportOut>(`${CLIENTS}/${client}/passport`, body),
  revisions: (client: string) => api<ClientPassportRevisionOut[]>(`${CLIENTS}/${client}/passport/revisions`),
};

/** Spec 062: the client's dossier and the summary of an order's chat, with their saved texts. */
export const ClientDossiers = {
  get: (client: string) => api<ClientDossierOut>(`${CLIENTS}/${client}/dossier`),
  save: (client: string, body: { body_md: string; version: number }) => patch<ClientDossierOut>(`${CLIENTS}/${client}/dossier`, body),
  revisions: (client: string) => api<ClientPassportRevisionOut[]>(`${CLIENTS}/${client}/dossier/revisions`),
};

export const ChatSummaries = {
  get: (order: string) => api<ChatSummaryOut>(`${ORDERS}/${order}/chat/summary`),
  save: (order: string, body: { body_md: string; version: number }) => patch<ChatSummaryOut>(`${ORDERS}/${order}/chat/summary`, body),
  revisions: (order: string) => api<ClientPassportRevisionOut[]>(`${ORDERS}/${order}/chat/summary/revisions`),
};

/** Spec 054, step 3b: what the client gave through the safe link, never a value. */
export interface AccessSecretOut { id: string; name: string; created_at: string; destroyed: boolean; version: number; mine: boolean }
export interface AccessUseOut { kind: string; at: string; who: string | null; note: string | null }
export interface ClientAccessOut {
  resource_id: string;
  kind: string;
  name: string;
  address: string | null;
  login: string | null;
  comment: string | null;
  order_id: string | null;
  order_label: string | null;
  added_at: string;
  secrets: AccessSecretOut[];
  used: AccessUseOut[];
}
export interface AccessLinkOut { url: string; expires_at: string; rule: string }

export const ClientAccesses = {
  list: (client: string) => api<ClientAccessOut[]>(`${CLIENTS}/${client}/accesses`),
  /** A new link each time: the code is shown once. */
  link: (order: string, what_md: string | null) => post<AccessLinkOut>(`${ORDERS}/${order}/access-links`, { what_md }),
};
